import { useMultipleLoanWarningModal } from "@/features/application/components";
import styles from "./ApplicationPage.module.scss";
import {
  OceanOfferCard,
  preliminaryChoiceStorage,
  useLoanOffer,
} from "@/features/offer";
import { useUser } from "@/features/user";
import { PageLoader } from "@/layouts/components/PageLoader/PageLoader";
import { Navigate, useNavigate } from "react-router-dom";
import { routes } from "@/routes/routes";
import { useLoanLimits } from "@/features/loan/hooks/useLoanLimits";
import { ApplicationLimitState } from "@/features/loan/types/loan";
import { useEffect, useMemo, useState } from "react";
import { useMultipleLoanRestrictionModal } from "../../components/MultipleLoanRestrictionModal";
import { trackingService, useTrackOnboardingCompleted } from "@/lib/tracking";
import { useApplication } from "../../hooks/useApplication";
import type { OfferChangesState } from "../../components/FinalOfferChangesBanner";
import { resolveChosenOffer } from "./resolveChosenOffer";
import {
  calculateCreditLineOfferDetails,
  calculateInstallmentLoanDetails,
} from "@/features/offer/hooks/loanOfferUtils";

type ContinueTriggers = "credit-line-landing" | "installment-landing";

// Tells the chat it opened this screen on an offer that turned out expired,
// so it asks before opening it again.
export interface FinalOfferReturnState {
  isFinalOfferOutOfSync: true;
}

const outOfSyncState: FinalOfferReturnState = { isFinalOfferOutOfSync: true };

export function ApplicationPage() {
  const navigate = useNavigate();

  const {
    creditLineOffer,
    installmentOffer,
    loanOfferState,
    isLoading: isLoanOffersStateLoading,
    isOfferExpired,
  } = useLoanOffer(true);

  const { loanLimits, isLoading: isLoanLimitsLoading } = useLoanLimits(true);
  const { user } = useUser();
  const {
    creditLineDraft,
    installmentDraft,
    setCreditLineAmount,
    setInstallmentAmount,
    setInstallmentNumberOfRates,
  } = useApplication();
  // The preliminary credit the user went to KYC with, if any.
  const choice = useMemo(
    () => preliminaryChoiceStorage.get(user?.id),
    [user?.id],
  );
  const [continueTrigger, setContinueTrigger] =
    useState<ContinueTriggers | null>(null);

  const handleConfirm = (trigger?: ContinueTriggers) => {
    const resolvedTrigger = trigger || continueTrigger;
    if (!resolvedTrigger) {
      return;
    }

    trackingService.trackEvent("offer_apply", { trigger: resolvedTrigger });

    // The offer's landing, after the derogation declaration when the chosen
    // amount needs one.
    const isCreditLine = resolvedTrigger === "credit-line-landing";
    const landing = isCreditLine
      ? routes.application.creditLineConfirmation
      : routes.application.installmentConfirmation;
    const isDerogation = isCreditLine
      ? calculateCreditLineOfferDetails(creditLineOffer, creditLineDraft)
          ?.isDerogation
      : calculateInstallmentLoanDetails(
          installmentOffer,
          installmentDraft?.amount,
          installmentDraft?.numberOfRates,
        )?.isDerogation;

    if (isDerogation) {
      navigate(routes.application.derogationDeclaration, {
        state: { returnTo: landing },
      });
      return;
    }
    navigate(landing);
  };

  const {
    open: openMultipleLoanWarningModal,
    modal: multipleLoanWarningModal,
  } = useMultipleLoanWarningModal({
    onConfirm: handleConfirm,
    maxContracts: loanLimits?.contractLimit || 0,
    durationDays: loanLimits?.durationDays || 0,
    closedContracts: loanLimits?.closedContracts || 0,
  });

  const {
    open: openMultipleLoanRestrictionModal,
    modal: multipleLoanRestrictionModal,
  } = useMultipleLoanRestrictionModal({
    expireInDays: loanLimits?.expireIn || 0,
    durationDays: loanLimits?.durationDays || 0,
    closedContracts: loanLimits?.closedContracts || 0,
    maxContracts: loanLimits?.contractLimit || 0,
  });

  const handleContinue = (trigger: ContinueTriggers) => {
    setContinueTrigger(trigger);

    if (
      loanLimits?.applicationLimitState === ApplicationLimitState.Restricted
    ) {
      openMultipleLoanRestrictionModal();
      return;
    }

    if (
      loanLimits?.applicationLimitState === ApplicationLimitState.Penultimate
    ) {
      openMultipleLoanWarningModal();
      return;
    }

    handleConfirm(trigger);
  };

  const isPageLoading = isLoanOffersStateLoading || isLoanLimitsLoading;
  const hasOffer = Boolean(creditLineOffer || installmentOffer);
  // The chat asks for the consents again and requests a new offer.
  const hasOfferExpired = isOfferExpired && !!loanOfferState?.expiresAt;
  // Other loans first: the offers list asks about them before any credit.
  const isLimited =
    loanLimits?.applicationLimitState === ApplicationLimitState.Restricted ||
    loanLimits?.applicationLimitState === ApplicationLimitState.Penultimate;
  const isChoiceUsable =
    !!choice && !isPageLoading && !hasOfferExpired && hasOffer;
  const chosenNext =
    isChoiceUsable && !isLimited
      ? resolveChosenOffer({
          choice,
          creditLineOffer,
          installmentOffer,
          preliminaryOffers: loanOfferState?.preliminaryOffers,
        })
      : null;

  // Continues with the chosen credit once, then the choice is gone.
  useEffect(() => {
    if (!isChoiceUsable) {
      return;
    }
    preliminaryChoiceStorage.clear();
    if (!chosenNext) {
      return;
    }

    void trackingService.trackEvent("preliminary_choice_continued", {
      type: choice?.type,
      outcome:
        chosenNext.kind === "alternative"
          ? "alternative"
          : chosenNext.changes
            ? "changed"
            : "unchanged",
    });

    if (chosenNext.kind === "alternative") {
      navigate(routes.application.alternativeOffer, { replace: true });
      return;
    }

    if (chosenNext.type === "credit-line") {
      setCreditLineAmount(chosenNext.amount);
    } else {
      setInstallmentAmount(chosenNext.amount);
      if (chosenNext.numberOfRates) {
        setInstallmentNumberOfRates(chosenNext.numberOfRates);
      }
    }

    const confirmation =
      chosenNext.type === "credit-line"
        ? routes.application.creditLineConfirmation
        : routes.application.installmentConfirmation;
    const state: OfferChangesState = { offerChanges: chosenNext.changes };
    navigate(
      chosenNext.isDerogation
        ? routes.application.derogationDeclaration
        : confirmation,
      {
        replace: true,
        state: chosenNext.isDerogation
          ? { ...state, returnTo: confirmation }
          : state,
      },
    );
  }, [isChoiceUsable]);

  // Skipped while loading or when redirecting away, so the event only fires
  // once the offers are actually on screen.
  useTrackOnboardingCompleted(!isPageLoading && hasOffer && !hasOfferExpired);

  if (isPageLoading) {
    return <PageLoader />;
  }

  if (hasOfferExpired) {
    return <Navigate to={routes.wizard.base} replace state={outOfSyncState} />;
  }

  if (!hasOffer) {
    return <Navigate to={routes.offer.notAvailable} replace />;
  }

  // Leaving for the chosen credit: the list is not shown on the way.
  if (chosenNext) {
    return <PageLoader />;
  }

  return (
    <>
      <div className={styles.root}>
        <div className={styles.content}>
          <h1 className={styles.title}>Ofertele tale finale 🎉</h1>

          <div className={styles.offers}>
            {creditLineOffer && (
              <OceanOfferCard
                type="credit-line"
                amount={creditLineOffer.creditLimit}
                numberOfRates={creditLineOffer.numberOfRates}
                trackingId="final_offer_details_credit-line"
                onDetailsClick={() => handleContinue("credit-line-landing")}
              />
            )}
            {installmentOffer && (
              <OceanOfferCard
                type="installment"
                amount={installmentOffer.creditLimit}
                numberOfRates={installmentOffer.numberOfRates}
                trackingId="final_offer_details_installment"
                onDetailsClick={() => handleContinue("installment-landing")}
              />
            )}
          </div>
        </div>
      </div>
      {multipleLoanWarningModal}
      {multipleLoanRestrictionModal}
    </>
  );
}
